// HU-49 · Ampliar el informe con la información adicional del EA grave (rev. 13, 11 criterios).
//
// UNA PESTAÑA DE LA EVALUACIÓN, SOLO PARA LOS GRAVES (C6). La pestaña «Ampliar informe
// (act. 8)» no se muestra en un caso no grave: la decide `ampliacion_habilitada`.
//
// EL NUMERAL 9.10 ES EL INSTRUCTIVO (C8, RN-26). La sección «Información del numeral 9.10
// (Res. 2528)» muestra las 29 respuestas de HU-12 en SOLO LECTURA, con su numeración. No
// hay campo para volver a capturarlas, y ya no aparece la nota «se tipificará al recibir el
// numeral 9.10» (C8b). Los únicos textos libres son «Factores desencadenantes» y el
// «Sustento técnico y bibliográfico»; «Información adicional del evento» queda para lo que
// 9.10 no cubre.
//
// EL SUSTENTO (C2/C3/C10). Cada entrada es una cita y puede llevar VARIOS anexos
// (22-sep-2026: antes cabía uno solo y elegir otro lo reemplazaba en silencio). Cada anexo
// se sube en base64 con su propia casilla «Contiene datos de personas» (RN-09) y el
// servidor lo replica al expediente del caso con origen «Ampliación del informe (act. 8)».
// Una cita sin archivo también vale.
//
// DOS CANALES, NO UNO. El PUT de la ampliación decide QUÉ ENTRADAS hay y qué dice cada una
// —y borra la que ya no venga, con sus anexos—; los archivos van por su propio endpoint,
// uno por petición. Por eso un anexo solo cuelga de una entrada YA GUARDADA: en una entrada
// recién añadida «Adjuntar» queda apagado y pide guardar primero (O-09, QA 29-sep-2026).
// Antes la pantalla guardaba por su cuenta al adjuntar, y eso registraba la ampliación
// ENTERA —con lo que hubiera a medio escribir— sin que nadie pulsara «Guardar» (C1).
//
// ADJUNTAR Y QUITAR CONTESTAN LA AMPLIACIÓN ENTERA, y se manejan igual (`asentarLosAnexos`):
// la numeración de los anexos es PLANA a través de todas las citas, así que colgar un
// archivo de la primera corre los números de las siguientes tanto como borrarlo.
//
// EL CUPO POR ENTRADA Y LOS FORMATOS SALEN DE `parametro` y llegan con la ampliación y con
// la evaluación. Si no llegan, no se pre-valida y contesta el servidor: un número escrito
// aquí anunciaría un tope que no es el de la base.
//
// VOLVER A AMPLIAR CON EL FAR-03 APROBADO reabre el informe como borrador de la versión
// siguiente (C7): se avisa aquí y la pestaña «Informe FAR-03» lo recoge.
//
// ADJUNTAR O QUITAR UN ANEXO CON EL FAR-03 APROBADO TAMBIÉN LO REABRE (BUG-O09,
// `ui/MG-ILE-916.md`, tarea 916-2). Por eso, cuando el servidor entrega
// `texto_de_confirmacion_de_anexo` (solo con el FAR-03 aprobado), se pide confirmar ANTES de
// llamar al servidor, con ese texto tal cual; sin confirmar no se sube ni se quita nada. La
// respuesta trae `informe_reabierto.texto`, que se muestra como aviso. Reclasificar un anexo
// ya subido («Contiene datos de personas», tarea 916-4) no pregunta ni reabre (C2).
//
// SOLO LECTURA CON EL INFORME EN REVISIÓN (BUG-898-C4): el aviso es
// `texto_de_solo_lectura` del servidor; los campos quedan deshabilitados y los botones de
// escritura no se ofrecen. «Descargar» sigue disponible.

import { useCallback, useEffect, useRef, useState } from 'react';
import { comoFechaYHora } from '../lib/fechas';
import type { ChangeEvent } from 'react';
import { AlertTriangle, BookOpen, Download, FileUp, Lock, Paperclip, Plus, Save, Trash2 } from 'lucide-react';
import { Alert, Button, Card, Checkbox, Field, Input, L2Tag, Modal, Mono, Select, Textarea, tamanoLegible } from './ui';
import { AVISO_FALTA_AMPLIACION, TITULO_DEL_NUMERAL_910, adjuntarAnexo, ampliarInforme, descargarAnexo, mensajeDeTopeSuperado, quitarAnexo, reclasificarAnexo, topeDeTexto, verAmpliacion } from '../api/evaluacion';
import type { Ampliacion, AnexoDeSustento, EntradaDeSustento, Evaluacion, ReferenciaTecnica, RespuestaDelNumeral } from '../api/evaluacion';
import { INSTRUCTIVO } from '../data/instructivo';
import RespuestasDelInstructivo from './RespuestasDelInstructivo';
import { listarCatalogo } from '../api/catalogos';
import type { RespuestaDeCatalogo } from '../api/catalogos';
import { archivoABase64 } from '../api/plantillas';
import { mensajeDeCupoLleno, problemaDelAnexo, textoDeLosLimites } from '../lib/anexos';
import type { LimitesDeAnexo } from '../lib/anexos';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { descargarBlob } from '../lib/entradas';

type Opcion = { value: string; label: string };

/** BUG-O09: el acto que espera confirmación (con el FAR-03 aprobado), con lo que hace falta para consumarlo. */
type AnexoPorConfirmar =
  | { tipo: 'adjuntar'; fila: FilaDeSustento; archivo: File }
  | { tipo: 'quitar'; fila: FilaDeSustento; anexo: AnexoDeSustento };

/**
 * Una fila del editor del sustento.
 *
 * `id` es la IDENTIDAD DEL SERVIDOR —lo que el PUT manda y de lo que cuelgan los anexos—, y
 * es `null` mientras la entrada solo existe en pantalla. `clave` es aparte y solo para
 * React: dos entradas recién añadidas comparten `id` nulo, y con eso por `key` la lista se
 * repinta mal y el foco salta de campo.
 */
type FilaDeSustento = {
  clave: string;
  id: string | null;
  cita: string;
  tipo: string;
  anexos: AnexoDeSustento[];
};

/** Las opciones de un desplegable desde un catálogo: solo lo activo, con el nombre o, a falta de él, el código. */
const opcionesDelCatalogo = (r: RespuestaDeCatalogo): Opcion[] =>
  r.valores.filter((v) => v.activo !== false).map((v) => ({ value: v.codigo, label: v.nombre ?? v.codigo }));

const nuevaFila = (): FilaDeSustento => ({ clave: crypto.randomUUID(), id: null, cita: '', tipo: '', anexos: [] });

// La `clave` de una entrada ya guardada es su `id`: estable entre lecturas. Con una clave al
// azar en cada lectura, una segunda lectura (el doble montaje de desarrollo, o la que sigue a
// abrir la pestaña) volvía a crear las filas y el selector de archivo en el que se acababa de
// elegir uno dejaba de existir: el archivo no se adjuntaba (QA 01-10, visto en la E2E de 916).
const desdeReferencia = (r: ReferenciaTecnica): FilaDeSustento => ({
  clave: r.id ?? crypto.randomUUID(),
  id: r.id ?? null,
  cita: r.cita,
  tipo: r.tipo ?? '',
  anexos: r.anexos ?? [],
});

/**
 * Refresca SOLO los anexos de las filas que ya existen en el servidor, conservando lo que
 * el usuario tiene escrito. Adjuntar y quitar devuelven la ampliación entera —la numeración
 * es plana a través de TODAS las citas, así que tocar una renumera las demás—, y rehacer
 * las filas desde ella borraría la cita a medio escribir de cualquier otra entrada.
 */
const conLosAnexosDelServidor = (filas: FilaDeSustento[], referencias: ReferenciaTecnica[]): FilaDeSustento[] => {
  const porId = new Map(referencias.filter((r) => r.id).map((r) => [r.id, r.anexos ?? []]));
  return filas.map((f) => (f.id && porId.has(f.id) ? { ...f, anexos: porId.get(f.id) ?? [] } : f));
};

/** Los topes de los textos libres y de cada cita (parámetro `evaluacion_limites_de_texto`). */
type TopesDeLaAmpliacion = { factores?: number; informacion?: number; cita?: number };
type ExcesosDeTexto = { factores?: string; informacion?: string };

/** Qué excede su tope antes de enviar: el mensaje de cada texto va bajo su campo y el de cada cita bajo su fila. */
const excesosDeTope = (factores: string, informacion: string, filas: FilaDeSustento[], topes: TopesDeLaAmpliacion): { texto: ExcesosDeTexto; citas: Record<string, string> } => {
  const texto: ExcesosDeTexto = {};
  if (topes.factores && factores.trim().length > topes.factores) texto.factores = mensajeDeTopeSuperado('Factores desencadenantes', factores.trim().length, topes.factores);
  if (topes.informacion && informacion.trim().length > topes.informacion) texto.informacion = mensajeDeTopeSuperado('Información adicional del evento', informacion.trim().length, topes.informacion);
  const citas: Record<string, string> = {};
  filas.forEach((f, i) => {
    if (topes.cita && f.cita.trim().length > topes.cita) citas[f.clave] = mensajeDeTopeSuperado(`Cita ${i + 1}`, f.cita.trim().length, topes.cita);
  });
  return { texto, citas };
};

/**
 * Por qué «Adjuntar» está apagado en una entrada, dicho donde está el botón; `null` si se
 * puede. El orden es el de lo que el usuario tiene que hacer: escribir la cita, guardarla
 * (O-09: el anexo cuelga del `id` de una entrada guardada) y, ya guardada, respetar el cupo.
 */
const porQueNoAdjuntar = (f: FilaDeSustento, sinCita: boolean, cupoLleno: string | null): string | null => {
  if (sinCita) return 'Escribe la cita para poder adjuntar';
  if (!f.id) return 'Guarda la ampliación para poder adjuntar a esta entrada';
  return cupoLleno;
};

/** Una fila como la espera el PUT: qué entrada es y qué dice. Los archivos van por su propio endpoint. */
const entradaDeSustento = (f: FilaDeSustento): EntradaDeSustento => ({ id: f.id, cita: f.cita.trim(), tipo: f.tipo || null });

/**
 * Orden por puntos de codigo, estable en cualquier maquina.
 *
 * Se escribe con `if` y no con una ternaria anidada porque anidar ternarias es
 * `typescript:S3358`: el arreglo de una regla no deberia estrenar otra.
 */
const enOrdenEstable = (a: string, b: string): number => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

/**
 * Lo que hay escrito ahora, como una sola cadena comparable. Sirve para dos cosas que el
 * formulario no hacía (operador, 22-sep-2026): avisar de los cambios sin guardar al salir
 * de la pestaña, y decir cuándo no hay nada nuevo que guardar — antes se podía pulsar una y
 * otra vez y cada pulsación era un PUT que no cambiaba nada.
 *
 * Los ids de los anexos ENTRAN en la huella: adjuntar y quitar son cambios consumados en el
 * servidor, y sin ellos la comparación contra lo guardado quedaba desfasada.
 */
const huellaDeLaAmpliacion = (factores: string, informacion: string, evidencias: string[], filas: FilaDeSustento[]): string =>
  JSON.stringify([
    factores.trim(),
    informacion.trim(),
    // ORDEN ESTABLE, Y A PROPOSITO NO `localeCompare` (23-sep-2026).
    //
    // `sort()` sin comparador es `typescript:S2871` y el mensaje de Sonar sugiere
    // `localeCompare`. Aqui seria PEOR que el defecto: esto no es una lista que alguien
    // lea, es una HUELLA para detectar cambios sin guardar. Un orden que dependa del
    // locale hace que la misma ampliacion produzca huellas distintas en dos navegadores
    // con configuracion regional distinta, y entonces el formulario avisa de cambios que
    // nadie hizo. Lo que esta huella necesita es ser DETERMINISTA, no alfabetica en
    // espanol. La comparacion por puntos de codigo lo es, en cualquier maquina.
    [...evidencias].sort(enOrdenEstable),
    filas.map((f) => [f.cita.trim(), f.tipo, f.id, f.anexos.map((a) => a.id)]),
  ]);

/** B4: lo que se dice junto a «Guardar»: por qué está apagado o qué pasará al guardar. */
const textoJuntoAGuardar = (registrada: boolean, sucio: boolean): string => {
  if (sucio) return registrada ? 'Al guardar, si el FAR-03 ya estaba aprobado, vuelve a borrador y hay que aprobarlo de nuevo.' : '';
  return registrada
    ? 'Sin cambios desde el último guardado.'
    : 'Escribe los factores o la información adicional, marca una evidencia o agrega una entrada de sustento para ampliar el informe.';
};

/** El servidor nombra la cita por su posición («Cita 2 supera el máximo…»): ese 422 va bajo esa fila, no arriba. */
const filaDeLaCitaRechazada = (f: Fallo, filas: FilaDeSustento[]): FilaDeSustento | undefined => {
  const posicion = f.estado === 422 ? /Cita (\d+) supera el máximo/.exec(f.message) : null;
  return posicion ? filas[Number(posicion[1]) - 1] : undefined;
};

/** El numeral 9.10 tal como se pinta: el título, las 29 respuestas y el avance. */
type NumeralDelInstructivo = { titulo: string; respuestas: RespuestaDelNumeral[]; respondidos: number; total: number };

/**
 * C8: el numeral SIEMPRE se muestra, haya o no ampliación guardada. Si el servidor la
 * trae (200, con o sin `esta_registrada`), manda la suya; si respondió 404 —caso aún
 * sin ampliar— se arma desde las respuestas del padre (HU-12), que son las mismas.
 * Antes solo aparecía tras el primer guardado (observado en la E2E del 12-sep-2026).
 */
// El NOMBRE de cada punto sale de `INSTRUCTIVO` (src/data), que es lo que ve la pestaña
// «Investigación»: el catálogo de la base llegó con «Punto N» y así se pintaba aquí
// (operador, 18-sep-2026). Lo que la base no tenga, conserva lo que mande el servidor.
const TITULOS_DEL_INSTRUCTIVO = new Map(INSTRUCTIVO.map((p) => [p.n, { corto: p.corto, texto: p.texto }]));
const conNombreReal = (r: RespuestaDelNumeral): RespuestaDelNumeral => {
  const propio = TITULOS_DEL_INSTRUCTIVO.get(r.numero);
  return propio ? { ...r, corto: propio.corto, texto: propio.texto } : r;
};

const numeralDesde = (ampliacion: Ampliacion | null, evaluacion: Evaluacion | undefined): NumeralDelInstructivo | null => {
  if (ampliacion && ampliacion.respuestas_del_instructivo?.length) {
    return { titulo: ampliacion.titulo_del_numeral, respuestas: ampliacion.respuestas_del_instructivo.map(conNombreReal), respondidos: ampliacion.respondidos, total: ampliacion.total };
  }
  if (!evaluacion) return null;
  const porPunto = new Map(evaluacion.respuestas.map((r) => [r.punto, r]));
  return {
    titulo: TITULO_DEL_NUMERAL_910,
    respondidos: evaluacion.respondidos,
    total: evaluacion.total,
    respuestas: INSTRUCTIVO.map((p) => {
      const r = porPunto.get(p.n);
      return {
        numero: p.n, corto: p.corto, texto: p.texto, valor: r?.valor ?? null, observacion: r?.observacion ?? null,
        derivada: r?.derivada ?? false, observacion_protegida: r?.observacion_protegida ?? false,
      };
    }),
  };
};

function Numeral({ numeral }: { numeral: NumeralDelInstructivo }) {
  // B10 (QA 01-10-2026): pregunta, respuesta y comentario por bloques, como en el FAR-03.
  return (
    <Card title={numeral.titulo} icon={BookOpen}
      subtitle={`Las respuestas del instructivo, en solo lectura · ${numeral.respondidos} de ${numeral.total} puntos respondidos. Se corrigen en la pestaña «Investigación».`}>
      <RespuestasDelInstructivo contexto="ampliacion" nivelDelTitulo={3} aria-label={numeral.titulo}
        respuestas={numeral.respuestas.map((r) => ({ ...r, valor: r.valor ?? null }))} />
    </Card>
  );
}

export default function AmpliacionDelInforme({ casoId, actor, soloLectura, evaluacion, zonaHoraria, onCambio, onSucio }: {
  casoId: string; actor: string; soloLectura: boolean;
  /** RN-82: la zona del país del caso (`useZonaDelPais`), en la que se lee la hora; `null` = Lima. */
  zonaHoraria: string | null;
  /** Avisa al padre si hay algo escrito y sin guardar, como Investigación, Causalidad y Gravedad. */
  onSucio?: (sucio: boolean) => void;
  /** La evaluación del padre: sus 29 respuestas arman el numeral 9.10 cuando aún no hay ampliación (C8). */
  evaluacion?: Evaluacion;
  /** Se avisa al padre para que refresque `ampliacion_pendiente` y la pestaña del informe. */
  onCambio?: () => void;
}) {
  const [ampliacion, setAmpliacion] = useState<Ampliacion | null>(null);
  const [existe, setExiste] = useState<boolean | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [factores, setFactores] = useState('');
  const [informacion, setInformacion] = useState('');
  // Topes de `parametro` (`evaluacion_limites_de_texto`, 18-sep-2026): maxLength + contador;
  // el aviso bajo el campo cubre un texto ya guardado más largo que el tope vigente.
  const topeDeFactores = topeDeTexto(evaluacion, 'ampliacion_factores_caracteres');
  const topeDeInformacion = topeDeTexto(evaluacion, 'ampliacion_informacion_adicional_caracteres');
  // 21-sep-2026: cada «Cita» del sustento tiene su tope (`ampliacion_cita_caracteres`); el error va bajo su fila.
  const topeDeCita = topeDeTexto(evaluacion, 'ampliacion_cita_caracteres');
  const [erroresDeTexto, setErroresDeTexto] = useState<{ factores?: string; informacion?: string }>({});
  const [erroresDeCita, setErroresDeCita] = useState<Record<string, string>>({});
  // El archivo rechazado al elegirlo, por fila: el mensaje va donde está «Adjuntar».
  const [erroresDeArchivo, setErroresDeArchivo] = useState<Record<string, string>>({});
  /**
   * Lo ÚLTIMO QUE DIJO EL SERVIDOR, no su huella: adjuntar y quitar anexos cambian lo
   * guardado sin pasar por «Guardar», y con una huella ya calculada no había forma de
   * ponerla al día sin recalcularla desde datos que ya no se tenían. `null` mientras no se
   * ha leído nada todavía.
   */
  const [guardado, setGuardado] = useState<{ factores: string; informacion: string; evidencias: string[]; filas: FilaDeSustento[] } | null>(null);
  const cuentaAtras = (valor: string, tope?: number): string | undefined => (tope ? `${valor.length} / ${tope} caracteres` : undefined);
  const [evidencias, setEvidencias] = useState<string[]>([]);
  const [filas, setFilas] = useState<FilaDeSustento[]>([]);
  // La fila cuyo archivo está subiendo, y el anexo cuyo borrado está en vuelo: mientras
  // tanto sus botones se apagan, para no disparar dos veces la misma operación.
  const [subiendoEn, setSubiendoEn] = useState<string | null>(null);
  const [quitandoAnexo, setQuitandoAnexo] = useState<string | null>(null);
  // La casilla L2 (RN-09) del PRÓXIMO archivo de cada fila: se marca antes de elegirlo y
  // viaja con él. Es del archivo, no de la entrada, así que se apaga tras cada subida.
  const [l2DelProximo, setL2DelProximo] = useState<Record<string, boolean>>({});
  // BUG-O09: lo que espera confirmación y el aviso de la versión que el acto reabrió.
  const [porConfirmar, setPorConfirmar] = useState<AnexoPorConfirmar | null>(null);
  const [reabierto, setReabierto] = useState<string | null>(null);
  // 916-4: el anexo cuya marca L2 se está cambiando.
  const [reclasificando, setReclasificando] = useState<string | null>(null);
  const selectores = useRef<Record<string, HTMLInputElement | null>>({});
  const [tiposDeEvidencia, setTiposDeEvidencia] = useState<Opcion[]>([]);
  const [tiposDeReferencia, setTiposDeReferencia] = useState<Opcion[]>([]);
  // HU-49-C1: un catálogo que no carga se DICE. Antes se tragaba en silencio y la
  // pantalla mostraba «Sin catálogo de evidencias cargado» como si fuera un dato.
  const [avisosDeCatalogo, setAvisosDeCatalogo] = useState<string[]>([]);

  // HU-49-C2/C10 · QUÉ ARCHIVO SE PUEDE ADJUNTAR, DICHO POR EL SERVIDOR (22-sep-2026).
  //
  // El selector ofrecía `.pdf,.doc,.docx,image/*` y anunciaba «máx. 10 MB», y las dos cosas
  // eran falsas: el servidor rechazaba Word y cualquier imagen que no fuera jpeg/png/tiff/
  // heic —un .webp, sin ir más lejos— y su tope real son los 20 MB de `parametro`. Ahora la
  // regla es la de HU-07 (lista negra de ejecutables) y viene de la base: el formato y el
  // tamaño con la evaluación, el cupo por entrada con la ampliación. Lo que no llegue, no
  // se pre-valida.
  const cupoDeAnexos = ampliacion?.anexo_cantidad_maxima;
  const limitesDelAnexo: LimitesDeAnexo = {
    anexo_extensiones_bloqueadas: evaluacion?.anexo_extensiones_bloqueadas,
    anexo_tamano_maximo_mb: evaluacion?.anexo_tamano_maximo_mb,
    anexo_cantidad_maxima: cupoDeAnexos,
  };

  /**
   * Solo cuenta la lectura VIGENTE. Cada lectura toma un turno y el desmontaje (también el que
   * StrictMode simula en desarrollo) invalida los anteriores: una respuesta vieja que llega tarde
   * no se adopta. Adoptarla rehacía las filas con claves nuevas y pisaba lo que el usuario ya
   * estaba haciendo: el selector de archivo en uso quedaba fuera del documento y el archivo
   * elegido no llegaba a la confirmación del BUG-O09 (MG-ILE-916, «Estados»).
   */
  const lecturaVigente = useRef(0);
  const cargar = useCallback(async () => {
    const turno = ++lecturaVigente.current;
    setFallo(null);
    try {
      const a = await verAmpliacion(casoId);
      if (turno !== lecturaVigente.current) return;
      setAmpliacion(a);
      // El servidor responde 200 con `esta_registrada=false` (y las 29 respuestas) para un
      // caso aún sin ampliar; el 404 se conserva por si un despliegue viejo lo sigue dando.
      setExiste(a.esta_registrada !== false);
      setFactores(a.factores ?? '');
      setInformacion(a.informacion_adicional ?? '');
      setEvidencias(a.evidencias_revisadas);
      const filasLeidas = a.referencias.map(desdeReferencia);
      setFilas(filasLeidas);
      setGuardado({ factores: a.factores ?? '', informacion: a.informacion_adicional ?? '', evidencias: a.evidencias_revisadas, filas: filasLeidas });
      // B4: sin cambios ya no se guarda, así que lo YA guardado por encima del tope vigente
      // (el parámetro pudo bajar) se señala al abrir, bajo su campo, y no al pulsar «Guardar».
      const excesos = excesosDeTope(a.factores ?? '', a.informacion_adicional ?? '', filasLeidas, { factores: topeDeFactores, informacion: topeDeInformacion, cita: topeDeCita });
      setErroresDeTexto(excesos.texto);
      setErroresDeCita(excesos.citas);
    } catch (e) {
      if (turno !== lecturaVigente.current) return;
      const f = aFallo(e);
      // 404: caso aun sin ampliar. El estado guardado del vacio tambien se fija, o no
      // habria contra que comparar para saber si hay cambios sin guardar.
      if (f.estado === 404) {
        setExiste(false);
        setAmpliacion(null);
        setGuardado({ factores: '', informacion: '', evidencias: [], filas: [] });
        return;
      }
      setFallo(f);
    }
  }, [casoId, topeDeFactores, topeDeInformacion, topeDeCita]);

  useEffect(() => {
    cargar();
    return () => { lecturaVigente.current += 1; };
  }, [cargar]);
  useEffect(() => {
    let vivo = true;
    const alFallarCatalogo = (nombre: string, e: unknown) => {
      if (vivo) setAvisosDeCatalogo((a) => [...a, `No se pudo cargar el catálogo «${nombre}»: ${aFallo(e).message}`]);
    };
    listarCatalogo('tipo_evidencia_revisada')
      .then((r) => { if (vivo) setTiposDeEvidencia(opcionesDelCatalogo(r)); })
      .catch((e: unknown) => alFallarCatalogo('tipo_evidencia_revisada', e));
    listarCatalogo('tipo_referencia')
      .then((r) => { if (vivo) setTiposDeReferencia(opcionesDelCatalogo(r)); })
      .catch((e: unknown) => alFallarCatalogo('tipo_referencia', e));
    return () => { vivo = false; };
  }, []);

  const cambiarFila = (clave: string, cambio: Partial<FilaDeSustento>) =>
    setFilas((fs) => fs.map((f) => (f.clave === clave ? { ...f, ...cambio } : f)));
  const quitarFila = (clave: string) => setFilas((fs) => fs.filter((x) => x.clave !== clave));
  const olvidarErrorDeArchivo = (clave: string) =>
    setErroresDeArchivo((x) => { if (x[clave] === undefined) return x; const { [clave]: _quitado, ...resto } = x; return resto; });
  // La casilla de una evidencia: la mete en la lista de las revisadas o la saca.
  const marcarEvidencia = (valor: string, marcada: boolean) =>
    setEvidencias((ev) => (marcada ? [...ev, valor] : ev.filter((x) => x !== valor)));

  /** Guarda la ampliación. Solo lo dispara «Guardar ampliación» (C1, O-09). */
  const guardar = async () => {
    setFallo(null);
    setAviso(null);
    const excesos = excesosDeTope(factores, informacion, filas, { factores: topeDeFactores, informacion: topeDeInformacion, cita: topeDeCita });
    setErroresDeTexto(excesos.texto);
    setErroresDeCita(excesos.citas);
    if (excesos.texto.factores || excesos.texto.informacion || Object.keys(excesos.citas).length > 0) return;
    if (filas.some((f) => !f.cita.trim())) { setFallo({ message: 'Cada entrada del sustento necesita su cita, aunque no lleve archivo.' }); return; }
    setOcupado(true);
    try {
      const a = await ampliarInforme(casoId, {
        registrado_por: actor,
        factores: factores.trim() || null,
        informacion_adicional: informacion.trim() || null,
        evidencias_revisadas: evidencias,
        referencias: filas.map(entradaDeSustento),
      });
      setAmpliacion(a);
      setExiste(true);
      // La `clave` de cada fila SOBREVIVE al guardado: el servidor devuelve las entradas en
      // el orden en que se enviaron, así que la de la posición i es la misma fila. Si se
      // regenerara, todo lo que cuelga de ella —el error de su archivo, su casilla L2, el
      // «Subiendo…»— apuntaría a una fila que ya no existe, y adjuntar en una entrada recién
      // creada dejaba el fallo invisible.
      const filasGuardadas = a.referencias.map((r, i) => ({ ...desdeReferencia(r), clave: filas[i]?.clave ?? crypto.randomUUID() }));
      setFilas(filasGuardadas);
      setErroresDeArchivo({});
      setGuardado({ factores: a.factores ?? '', informacion: a.informacion_adicional ?? '', evidencias: a.evidencias_revisadas, filas: filasGuardadas });
      setAviso(a.informe_reabierto_en_version
        ? `Ampliación guardada. El FAR-03 volvió a borrador (v${a.informe_reabierto_en_version}): envíalo de nuevo a aprobación.`
        : 'Ampliación guardada.');
      onCambio?.();
    } catch (e) {
      const f = aFallo(e);
      const fila = filaDeLaCitaRechazada(f, filas);
      if (fila) setErroresDeCita({ [fila.clave]: f.message });
      else setFallo(f);
    } finally {
      setOcupado(false);
    }
  };

  /**
   * Adopta la ampliación que devuelven adjuntar y quitar. Las dos contestan lo mismo —la
   * ampliación entera— porque las dos renumeran: de ahí que el manejo sea uno solo.
   *
   * Se toman SOLO los anexos, no las citas: el usuario puede tener una escrita a medias en
   * otra entrada y adoptar la respuesta entera se la borraría. Y se anotan en los dos
   * sitios —lo que se ve y lo último que dijo el servidor—, porque adjuntar y quitar son
   * cambios YA consumados: contarlos como «sin guardar» sería mentir.
   */
  const asentarLosAnexos = (a: Ampliacion) => {
    // La respuesta de adjuntar o quitar trae la versión que el acto reabrió (BUG-O09).
    if (a.informe_reabierto) setReabierto(a.informe_reabierto.texto);
    setAmpliacion(a);
    setFilas((fs) => conLosAnexosDelServidor(fs, a.referencias));
    setGuardado((g) => (g ? { ...g, filas: conLosAnexosDelServidor(g.filas, a.referencias) } : g));
  };

  /**
   * Adjunta UN archivo a una entrada YA GUARDADA: el anexo cuelga de su `id`. Una entrada
   * sin guardar no llega aquí —su «Adjuntar» está apagado—, y si el archivo entrara igual
   * por el selector oculto, no se sube ni se guarda nada por detrás (O-09).
   */
  const adjuntarEnLaEntrada = async (fila: FilaDeSustento, archivo: File, confirmado = false) => {
    olvidarErrorDeArchivo(fila.clave);
    const referenciaId = fila.id;
    if (!referenciaId) return;
    const problema = problemaDelAnexo(archivo, limitesDelAnexo, fila.anexos);
    if (problema) { setErroresDeArchivo((x) => ({ ...x, [fila.clave]: problema })); return; }
    // BUG-O09-C1: con el FAR-03 aprobado se confirma ANTES de subir; sin confirmar no se llama.
    if (ampliacion?.texto_de_confirmacion_de_anexo && !confirmado) { setPorConfirmar({ tipo: 'adjuntar', fila, archivo }); return; }

    setSubiendoEn(fila.clave);
    setFallo(null);
    try {
      asentarLosAnexos(await adjuntarAnexo(casoId, referenciaId, archivo.name, await archivoABase64(archivo), archivo.type || null, l2DelProximo[fila.clave] ?? false));
      // La casilla es de ESE archivo: se apaga para que el siguiente no herede la marca.
      setL2DelProximo((x) => ({ ...x, [fila.clave]: false }));
      onCambio?.();
    } catch (e) {
      const f = aFallo(e);
      setErroresDeArchivo((x) => ({ ...x, [fila.clave]: f.message }));
    } finally {
      setSubiendoEn(null);
    }
  };

  /** Quita un anexo. El servidor devuelve la ampliación entera porque renumera lo que va detrás. */
  const quitarElAnexo = async (fila: FilaDeSustento, anexo: AnexoDeSustento, confirmado = false) => {
    // BUG-O09-C2: con el FAR-03 aprobado, quitar también reabre: se confirma antes.
    if (ampliacion?.texto_de_confirmacion_de_anexo && !confirmado) { setPorConfirmar({ tipo: 'quitar', fila, anexo }); return; }
    setFallo(null);
    setQuitandoAnexo(anexo.id);
    try {
      asentarLosAnexos(await quitarAnexo(casoId, anexo.id));
      onCambio?.();
    } catch (e) {
      const f = aFallo(e);
      setErroresDeArchivo((x) => ({ ...x, [fila.clave]: f.message }));
    } finally {
      setQuitandoAnexo(null);
    }
  };

  /** Consuma el acto confirmado en el cuadro (BUG-O09). */
  const confirmarElAnexo = () => {
    const pendiente = porConfirmar;
    setPorConfirmar(null);
    if (!pendiente) return;
    if (pendiente.tipo === 'adjuntar') adjuntarEnLaEntrada(pendiente.fila, pendiente.archivo, true);
    else quitarElAnexo(pendiente.fila, pendiente.anexo, true);
  };

  /**
   * HU-49-C10 / BUG-O09-C2 (tarea 916-4): marca o desmarca «Contiene datos de personas» en un
   * anexo YA subido. No pregunta: no reabre el FAR-03 aprobado (solo cambia la marca L2).
   */
  const reclasificar = async (fila: FilaDeSustento, anexo: AnexoDeSustento, contiene: boolean) => {
    olvidarErrorDeArchivo(fila.clave);
    setReclasificando(anexo.id);
    try {
      asentarLosAnexos(await reclasificarAnexo(casoId, anexo.id, contiene));
      onCambio?.();
    } catch (e) {
      const f = aFallo(e);
      setErroresDeArchivo((x) => ({ ...x, [fila.clave]: f.message }));
    } finally {
      setReclasificando(null);
    }
  };

  // CAMBIOS SIN GUARDAR: lo mismo que Investigación, Causalidad, Gravedad y Acciones.
  // Antes se salía de la pestaña y el sustento a medio llenar se perdía sin avisar.
  const sucio = guardado !== null
    && huellaDeLaAmpliacion(factores, informacion, evidencias, filas)
      !== huellaDeLaAmpliacion(guardado.factores, guardado.informacion, guardado.evidencias, guardado.filas);
  useEffect(() => { onSucio?.(sucio); }, [sucio, onSucio]);

  const descargar = async (anexo: AnexoDeSustento) => {
    setFallo(null);
    try {
      descargarBlob(await descargarAnexo(casoId, anexo.id), anexo.nombre_archivo);
    } catch (e) {
      const f = aFallo(e);
      setFallo(f.estado === 403 ? { ...f, message: `«${anexo.nombre_archivo}» contiene datos de personas (L2): necesitas el permiso L2 para descargarlo. El intento quedó registrado en la auditoría.` } : f);
    }
  };

  if (existe === null && !fallo) return <p data-testid="ampliacion-cargando" className="p-2 text-body text-ink-soft">Abriendo la ampliación…</p>;

  const numeral = numeralDesde(ampliacion, evaluacion);
  const limitesDichos = textoDeLosLimites(limitesDelAnexo);

  return (
    <div className="space-y-4">
      {fallo && (
        <Alert data-testid={existe === null ? 'ampliacion-error' : 'ampliacion-error-envio'} tone={fallo.estado === 403 ? 'warning' : 'danger'}
          title="No se pudo completar" detalleTecnico={fallo.detalleTecnico}>
          {fallo.message}
        </Alert>
      )}
      {/* BUG-898-C4: el aviso de solo lectura es el texto del servidor, tal cual. */}
      {soloLectura && ampliacion?.texto_de_solo_lectura && (
        <Alert data-testid="ampliacion-aviso-solo-lectura" tone="info" icon={Lock} title={ampliacion.texto_de_solo_lectura} />
      )}
      {avisosDeCatalogo.map((a) => <Alert key={a} tone="warning" title="Catálogo no disponible">{a}</Alert>)}
      {aviso && <Alert tone="success" title={aviso} onCerrar={() => setAviso(null)} />}
      {reabierto && <Alert data-testid="ampliacion-anexo-informe-reabierto" tone="success" title={reabierto} onCerrar={() => setReabierto(null)} />}
      {existe === false && (
        <Alert tone="warning" title={AVISO_FALTA_AMPLIACION} icon={AlertTriangle}>
          El caso es grave: la actividad 8 del procedimiento pide ampliar el informe. Hasta entonces el FAR-03 sale señalado, no oculto.
        </Alert>
      )}
      {ampliacion?.esta_registrada && (
        <p className="text-meta text-ink-soft">
          Ampliado por <span className="text-ink font-medium">{ampliacion.registrado_por}</span> el{' '}
          <Mono>{ampliacion.fecha ? comoFechaYHora(ampliacion.fecha, zonaHoraria) : '—'}</Mono>.
        </p>
      )}

      {numeral && <Numeral numeral={numeral} />}

      <Card title="Factores desencadenantes y evidencias revisadas" icon={FileUp}
        subtitle="Texto libre solo para lo que el numeral 9.10 no cubre.">
        <Field label="Factores desencadenantes" error={erroresDeTexto.factores} contador={cuentaAtras(factores, topeDeFactores)}>
          <Textarea data-testid="ampliacion-form-factores" aria-label="Factores desencadenantes" rows={3} value={factores} maxLength={topeDeFactores}
            onChange={(e) => { setFactores(e.target.value); setErroresDeTexto((x) => (x.factores ? { ...x, factores: undefined } : x)); }} disabled={soloLectura} />
        </Field>
        <Field label="Evidencias revisadas" hint="Marca lo que el Comité revisó.">
          <div className="flex flex-wrap gap-2">
            {tiposDeEvidencia.length === 0 && <span className="text-meta text-ink-soft">Sin catálogo de evidencias cargado.</span>}
            {tiposDeEvidencia.map((t) => (
              // En solo lectura la casilla NO se marca (22-sep-2026): era el unico control de
              // la pestana que quedaba vivo, y como el boton «Guardar» no se pinta, marcarla
              // no guardaba nada. Un control que parece editable y no lo es miente.
              <Checkbox key={t.value} label={t.label} checked={evidencias.includes(t.value)} disabled={soloLectura}
                onChange={(e: ChangeEvent<HTMLInputElement>) => marcarEvidencia(t.value, e.target.checked)} />
            ))}
          </div>
        </Field>
        <Field label="Información adicional del evento" hint="Solo lo que las 29 respuestas no recogen." error={erroresDeTexto.informacion} contador={cuentaAtras(informacion, topeDeInformacion)}>
          <Textarea data-testid="ampliacion-form-informacion" aria-label="Información adicional del evento" rows={3} value={informacion} maxLength={topeDeInformacion}
            onChange={(e) => { setInformacion(e.target.value); setErroresDeTexto((x) => (x.informacion ? { ...x, informacion: undefined } : x)); }} disabled={soloLectura} />
        </Field>
      </Card>

      <Card title="Sustento técnico y bibliográfico" icon={Paperclip}
        subtitle="Cada entrada es una cita y puede llevar varios anexos. Una cita sin archivo también vale."
        actions={!soloLectura && <Button data-testid="ampliacion-agregar-entrada" size="sm" variant="secondary" icon={Plus} onClick={() => setFilas((fs) => [...fs, nuevaFila()])}>Agregar entrada</Button>}>
        {filas.length === 0 && <p className="text-meta text-ink-soft">Sin entradas de sustento.</p>}
        <ol className="space-y-3" aria-label="Sustento técnico y bibliográfico">
          {filas.map((f, i) => {
            const sinCita = !f.cita.trim();
            const cupoLleno = !!cupoDeAnexos && f.anexos.length >= cupoDeAnexos;
            // Por qué «Adjuntar» está apagado, dicho donde está el botón. Un botón gris y
            // mudo se vive como una pantalla rota; aquí dice qué hacer para encenderlo.
            const porQueNoSePuedeAdjuntar = porQueNoAdjuntar(f, sinCita, cupoLleno ? mensajeDeCupoLleno(f.anexos.length, cupoDeAnexos ?? 0) : null);
            return (
              <li key={f.clave} className="border border-hairline rounded-xl p-3">
                <div className="flex flex-wrap items-start gap-3">
                  <Mono className="text-meta pt-2">{i + 1}.</Mono>
                  <div className="flex-1 min-w-[16rem]">
                    <Field label="Cita" required error={erroresDeCita[f.clave]} contador={cuentaAtras(f.cita, topeDeCita)}>
                      <Input data-testid="ampliacion-form-cita" aria-label={`Cita ${i + 1}`} value={f.cita} maxLength={topeDeCita} disabled={soloLectura}
                        onChange={(e) => { cambiarFila(f.clave, { cita: e.target.value }); if (erroresDeCita[f.clave]) setErroresDeCita((x) => { const { [f.clave]: _quitado, ...resto } = x; return resto; }); }} />
                    </Field>
                  </div>
                  <div className="w-56">
                    <Field label="Tipo">
                      {/* Apagado en solo lectura como la casilla de evidencias: sin «Guardar»
                          en pantalla, cambiarlo no guardaba nada (22-sep-2026). */}
                      <Select aria-label={`Tipo ${i + 1}`} placeholder="Elegir…" value={f.tipo} options={tiposDeReferencia}
                        disabled={soloLectura}
                        onChange={(e) => cambiarFila(f.clave, { tipo: String(e.target.value ?? '') })} />
                    </Field>
                  </div>
                  {!soloLectura && (
                    <Button size="sm" variant="secondary" icon={Trash2} onClick={() => quitarFila(f.clave)} aria-label={`Quitar entrada ${i + 1}`}>Quitar</Button>
                  )}
                </div>

                {/* LOS ANEXOS DE LA ENTRADA. Cada uno con su identidad, su tamaño y su marca
                    L2: elegir otro archivo YA NO reemplaza, añade (operador, 22-sep-2026). */}
                {f.anexos.length > 0 && (
                  <ul className="mt-2 space-y-1" aria-label={`Anexos de la entrada ${i + 1}`}>
                    {f.anexos.map((a) => (
                      <li key={a.id} data-testid="ampliacion-anexo-fila" className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 bg-canvas border border-hairline rounded-xl text-meta">
                        <Paperclip size={13} className="text-ink-soft" aria-hidden="true" />
                        <span className="font-medium">{a.etiqueta || a.nombre_archivo}</span>
                        <span className="text-ink-soft whitespace-nowrap">{tamanoLegible(a.tamano_bytes)}</span>
                        {a.contiene_dato_de_personas && <L2Tag>Datos de personas · L2</L2Tag>}
                        {!soloLectura && (
                          // 916-4: la marca L2 de un anexo YA subido se cambia aquí, sin volver a
                          // subirlo y sin reabrir el FAR-03 (BUG-O09-C2, HU-49-C10).
                          <Checkbox data-testid="ampliacion-anexo-l2" label="Contiene datos de personas"
                            aria-label={`«${a.etiqueta || a.nombre_archivo}» contiene datos de personas`}
                            checked={a.contiene_dato_de_personas} disabled={reclasificando !== null}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => { reclasificar(f, a, e.target.checked); }} />
                        )}
                        <span className="ml-auto flex items-center gap-2">
                          <Button size="sm" variant="secondary" icon={Download}
                            aria-label={`Descargar «${a.etiqueta || a.nombre_archivo}»`}
                            onClick={() => { descargar(a); }}>Descargar</Button>
                          {!soloLectura && (
                            <Button data-testid="ampliacion-anexo-quitar" size="sm" variant="secondary" icon={Trash2} disabled={quitandoAnexo !== null}
                              aria-label={`Quitar «${a.etiqueta || a.nombre_archivo}»`}
                              onClick={() => { quitarElAnexo(f, a); }}>
                              {quitandoAnexo === a.id ? 'Quitando…' : 'Quitar'}
                            </Button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {!soloLectura && (
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    {/* El selector real va OCULTO tras «Adjuntar», como en «Registrar caso»:
                        el control del sistema operativo junto al nuestro duplicaba el verbo
                        y nadie sabía cuál consumaba la acción. Sin `accept`: la lista negra
                        la aplica `parametro`, y una lista blanca escondería lo que el
                        servidor sí admite. */}
                    <input type="file" className="hidden" aria-hidden="true" tabIndex={-1}
                      data-entrada={i + 1} data-testid="ampliacion-adjuntar-archivo"
                      ref={(nodo) => { selectores.current[f.clave] = nodo; }}
                      onChange={(e) => {
                        const archivo = e.target.files?.[0] ?? null;
                        // Se limpia SIEMPRE: sin esto, volver a elegir el mismo archivo no
                        // dispara `change` y la pantalla parece colgada.
                        e.target.value = '';
                        if (archivo) adjuntarEnLaEntrada(f, archivo);
                      }} />
                    {/* La casilla va ANTES del botón, como en «Registrar caso»: es una
                        pregunta sobre el archivo que se va a elegir, y en cuanto se pulsa
                        «Adjuntar» el selector del sistema tapa la pantalla y ya no hay dónde
                        responderla. Es del PRÓXIMO archivo, no de la entrada, así que se
                        apaga sola en cuanto ese archivo sube (RN-09). No se pinta cuando no
                        se puede adjuntar: sería una pregunta sobre un archivo que no habrá. */}
                    {!porQueNoSePuedeAdjuntar && (
                      <Checkbox label="Contiene datos de personas" checked={l2DelProximo[f.clave] ?? false}
                        disabled={subiendoEn !== null}
                        hint="Se guarda con el archivo que elijas a continuación."
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setL2DelProximo((x) => ({ ...x, [f.clave]: e.target.checked }))} />
                    )}
                    <Button data-testid="ampliacion-adjuntar" size="sm" variant="secondary" icon={Paperclip}
                      disabled={porQueNoSePuedeAdjuntar !== null || subiendoEn !== null || ocupado}
                      title={porQueNoSePuedeAdjuntar ?? undefined}
                      aria-label={`Adjuntar un archivo a la entrada ${i + 1}`}
                      onClick={() => selectores.current[f.clave]?.click()}>
                      {subiendoEn === f.clave ? 'Subiendo…' : 'Adjuntar'}
                    </Button>
                    {porQueNoSePuedeAdjuntar && <span className="text-meta text-ink-soft">{porQueNoSePuedeAdjuntar}</span>}
                    {/* `basis-full`: la frase mide media pantalla y, en la misma línea que el
                        botón, partía la fila en dos alturas distintas según el ancho. */}
                    {f.anexos.length === 0 && !porQueNoSePuedeAdjuntar && (
                      <span data-testid="ampliacion-anexos-vacio" className="basis-full text-meta text-ink-soft">Lo que adjuntes queda en el expediente con origen «Ampliación del informe (act. 8)».</span>
                    )}
                  </div>
                )}
                {erroresDeArchivo[f.clave] && (
                  <div className="mt-2">
                    <Alert data-testid="ampliacion-error-envio" tone="warning" title="No se pudo adjuntar" onCerrar={() => olvidarErrorDeArchivo(f.clave)}>{erroresDeArchivo[f.clave]}</Alert>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        {/* Los límites, UNA sola vez: salían aquí y otra vez junto a cada selector, y el
            usuario los leía dos veces con palabras distintas (operador, 22-sep-2026). */}
        {!soloLectura && limitesDichos && <p className="mt-2 text-meta text-ink-soft">{limitesDichos}.</p>}
      </Card>

      {!soloLectura && (
        <div className="flex items-center justify-end gap-3">
          {/* B4 (QA 01-10-2026): sin cambios NO se guarda. Cada guardado deja autor y fecha
              nuevos y, con el FAR-03 aprobado, abre otra versión del informe; el servidor
              rechaza el guardado idéntico (y el de una ampliación vacía) y aquí el botón se
              apaga diciendo por qué. HU-49-C7 sigue igual: editar algo y guardar reabre el
              FAR-03 aprobado, y eso se anuncia ANTES de guardar. */}
          <span data-testid="ampliacion-guardar-estado" className="text-meta text-ink-soft" role="note">
            {textoJuntoAGuardar(existe === true, sucio)}
          </span>
          <Button data-testid="ampliacion-guardar" icon={Save} onClick={() => { guardar(); }} disabled={ocupado || !sucio}>
            {ocupado ? 'Guardando…' : (existe ? 'Guardar ampliación' : 'Ampliar informe (act. 8)')}
          </Button>
        </div>
      )}

      {/* BUG-O09: la confirmación previa. El título nombra el archivo o el anexo; el cuerpo
          es el texto del servidor tal cual; el pie, «Cancelar» y «Sí, verbo» (lineamiento,
          plantilla «Confirmación modal»). Cancelar no llama al servidor. */}
      <Modal data-testid="ampliacion-confirmar-anexo-modal" open={porConfirmar !== null} onClose={() => setPorConfirmar(null)}
        tone="warning" icon={AlertTriangle} eyebrow="Ampliar informe · Sustento"
        title={porConfirmar?.tipo === 'quitar'
          ? `¿Quitar «${porConfirmar.anexo.etiqueta || porConfirmar.anexo.nombre_archivo}»?`
          : `¿Adjuntar «${porConfirmar?.tipo === 'adjuntar' ? porConfirmar.archivo.name : ''}»?`}
        footer={<>
          <Button data-testid="ampliacion-cancelar-anexo" variant="secondary" onClick={() => setPorConfirmar(null)}>Cancelar</Button>
          <Button data-testid="ampliacion-confirmar-anexo" variant={porConfirmar?.tipo === 'quitar' ? 'danger' : 'primary'}
            icon={porConfirmar?.tipo === 'quitar' ? Trash2 : Paperclip} onClick={confirmarElAnexo}>
            {porConfirmar?.tipo === 'quitar' ? 'Sí, quitar' : 'Sí, adjuntar'}
          </Button>
        </>}>
        <p data-testid="ampliacion-confirmar-anexo-texto">{ampliacion?.texto_de_confirmacion_de_anexo}</p>
      </Modal>
    </div>
  );
}
